import {
  ApiHolder,
  configApiRef,
  fetchApiRef,
  useApi,
} from '@backstage/core-plugin-api';
import {
  CustomFieldExtensionSchema,
  FieldExtensionComponentProps,
} from '@backstage/plugin-scaffolder-react';
import { CatalogApi, catalogApiRef } from '@backstage/plugin-catalog-react';
import FormControl from '@material-ui/core/FormControl';
import Input from '@material-ui/core/Input';
import InputLabel from '@material-ui/core/InputLabel';
import { FormHelperText } from '@material-ui/core';
import { FieldValidation } from '@rjsf/utils';
import { useEffect, useState } from 'react';
import { CreateFactSheetPanel } from './CreateFactSheetPanel';

const DEFAULT_PATTERN = '^(appid-[0-9]{1,}|APPID-[0-9]{1,})$';
const DEFAULT_SANDBOX_OWNER = 'principalsbx-emu';

// Fixed validation messages. The field displays exactly one at a time
// (as-you-type); the submit-time validate function reuses the same messages to
// BLOCK "Next", but RJSF's own inline rendering is suppressed via
// `ui:hideError` in the template, so a message is never shown twice.
const REQUIRED_ERROR =
  'A LeanIX App ID is required for production services and websites. Enter an existing App ID, or complete the fact-sheet fields below to create one.';
const FORMAT_ERROR =
  'The value must start with `appid-` or `APPID-` followed by numbers, e.g. appid-12345678.';
const INCOMPLETE_ERROR =
  'Complete the fact-sheet fields — application name, owning org unit and IT custodian — or enter an existing App ID above.';
const CUSTODIAN_ERROR = 'Enter a valid IT Custodian email address.';
const AWS_REQUIRED_ERROR = 'Select whether this application is hosted on AWS.';

// Duplicate fact-sheet name. LeanIX enforces uniqueness on (display name,
// Application), so a taken name would fail creation at submit — this blocks
// Next until a unique name is entered.
const nameTakenError = (name: string): string =>
  `A LeanIX fact sheet named “${name}” already exists — please use a different name.`;

// Minimal email sanity check — Repo Foundry and LeanIX enforce the real rules.
const isEmail = (v: string): boolean => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v);

// The selected org travels in `repoUrl` as `...owner=<org>...` (RepoUrlPicker).
const getRepoOwner = (repoUrl?: string): string | undefined => {
  if (typeof repoUrl !== 'string') return undefined;
  return /owner=([^&]+)/.exec(repoUrl)?.[1];
};

// Captured at render so the validate function can reach the catalog even on
// older scaffolder-react versions that don't pass apiHolder in context.
let catalogApi: CatalogApi;

export const LeanIXAppIdExtensionSchema: CustomFieldExtensionSchema = {
  uiOptions: {
    type: 'object',
    required: ['fieldName'],
    properties: {
      fieldName: {
        description: 'The form field name this extension binds to.',
        type: 'string',
      },
      typeField: {
        description: 'Name of the component-type field to read from the form.',
        type: 'string',
        default: 'type',
      },
      lifecycleField: {
        description: 'Name of the lifecycle field to read from the form.',
        type: 'string',
        default: 'lifecycle',
      },
      requiredTypes: {
        description:
          'Component types for which the AppID is required (with requiredLifecycle).',
        type: 'array',
        items: { type: 'string' },
      },
      requiredLifecycle: {
        description: 'Lifecycle for which the AppID is required.',
        type: 'string',
        default: 'production',
      },
      pattern: {
        description: 'Regex the AppID must match when provided.',
        type: 'string',
      },
      validateInCatalog: {
        description:
          'Whether to verify the AppID exists as an application System in the catalog.',
        type: 'boolean',
        default: true,
      },
      repoUrlField: {
        description:
          'Name of the RepoUrlPicker field to read the selected org from (owner=<org>).',
        type: 'string',
        default: 'repoUrl',
      },
      sandboxOwner: {
        description:
          'Org treated as Sandbox: the AppID is never required there and the not-found message omits the LeanIX/ITGRC links.',
        type: 'string',
        default: 'principalsbx-emu',
      },
      guidance: {
        description:
          'Persistent guidance text rendered by the field itself. Deliberately NOT named `help`: RJSF treats `ui:options.help` as a reserved key and renders it via its own FieldHelpTemplate, which would duplicate this text. It is shown only while the field has no validation error, so an error message replaces it instead of appearing alongside it.',
        type: 'string',
      },
      panelGuidance: {
        description:
          'Extra guidance appended to `guidance` ONLY while the Create-fact-sheet panel is actually shown. Keep the sentence that references that panel here so it never appears for lifecycles/types where the panel is hidden.',
        type: 'string',
      },
      enableFactSheetCreation: {
        description:
          'When true, shows the inline "Create a LeanIX fact sheet" panel for users who do not yet have an App ID. The panel appears only for the governed (production service/website, non-sandbox) combination while the field is still empty.',
        type: 'boolean',
        default: false,
      },
    },
  },
  // Object-valued: either { appId } (existing) OR the fact-sheet inputs
  // { name, orgUnitId, custodianEmail, hostedOnAws } to create one at execution
  // time. The template passes the whole object to Repo Foundry.
  returnValue: { type: 'object' },
};

export type LeanIXAppIdOptions = {
  typeField?: string;
  lifecycleField?: string;
  requiredTypes?: string[];
  requiredLifecycle?: string;
  pattern?: string;
  validateInCatalog?: boolean;
  repoUrlField?: string;
  sandboxOwner?: string;
  guidance?: string;
  panelGuidance?: string;
  enableFactSheetCreation?: boolean;
};

// The fact-sheet inputs collected inline when the user has no App ID yet. They
// are carried in the field value so the template can pass them to Repo Foundry,
// which creates the fact sheet at execution time (Review -> Create).
export type LeanIXFactSheetInputs = {
  name?: string;
  orgUnitId?: string;
  custodianEmail?: string;
  hostedOnAws?: boolean;
  // LeanIX Application category key; defaults to businessApplication server-side.
  subtype?: string;
};

// The field is object-valued: either `appId` (an existing App ID) OR the
// fact-sheet inputs (to create one at execution time). Never a bare string.
export type LeanIXFieldValue = {
  appId?: string;
} & LeanIXFactSheetInputs;

/**
 * Single source of truth for the AppID validation. Returns the (one) error
 * message to show, or `undefined` when the value is acceptable. Both the
 * scaffolder `validation` function (submit-time, blocks progression) and the
 * field component (render-time, to hide the guidance while an error stands)
 * call this so the two can never disagree.
 */
export const computeLeanIXAppIdError = async (
  value: LeanIXFieldValue | undefined,
  ctx: {
    type?: string;
    lifecycle?: string;
    isSandbox?: boolean;
    options: LeanIXAppIdOptions;
    api?: CatalogApi;
    // Best-effort LeanIX name availability check. Returns true (free), false
    // (taken) or undefined (unknown). Only provided at submit-time so Next is
    // blocked on a duplicate; a failed/absent check never blocks.
    checkNameAvailable?: (name: string) => Promise<boolean | undefined>;
  },
): Promise<string | undefined> => {
  const requiredTypes = ctx.options.requiredTypes ?? ['service', 'website'];
  const requiredLifecycle = ctx.options.requiredLifecycle ?? 'production';
  const pattern = ctx.options.pattern ?? DEFAULT_PATTERN;
  const validateInCatalog = ctx.options.validateInCatalog !== false;

  // Sandbox is always exempt from the required rule: the App ID is never
  // mandatory for the Sandbox org (owner=principalsbx-emu).
  const isRequiredCombo =
    !ctx.isSandbox &&
    requiredTypes.includes(ctx.type as string) &&
    ctx.lifecycle === requiredLifecycle;

  const appId = (value?.appId ?? '').trim();
  const name = (value?.name ?? '').trim();
  const orgUnitId = (value?.orgUnitId ?? '').trim();
  const custodianEmail = (value?.custodianEmail ?? '').trim();

  // Path A — an existing App ID was entered: format + catalog checks apply and
  // the fact-sheet fields are ignored (Repo Foundry links it, does not create).
  if (appId.length > 0) {
    if (!new RegExp(pattern).test(appId)) {
      return FORMAT_ERROR;
    }
    if (validateInCatalog && ctx.api) {
      try {
        const { items } = await ctx.api.getEntities({
          filter: {
            kind: 'System',
            'spec.type': 'application',
            'metadata.name': appId.toLowerCase(),
          },
          fields: ['metadata.name'],
        });
        if (items.length === 0) {
          // Sandbox may always leave the field blank, so it gets the short hint;
          // every other org gets the LeanIX Factsheet / ITGRC verification links.
          const notFoundBase = `LeanIX AppID "${appId}" was not found as an application System in the PDP catalog. `;
          return ctx.isSandbox
            ? `${notFoundBase}Verify the ID, or leave blank if not applicable.`
            : `${notFoundBase}You can verify the AppId from the ExternalId field in the LeanIX Factsheet ` +
                '(https://principal.leanix.net/PrincipalProduction/#) or the ApplicationId from ITGRC ' +
                '(https://itgrc.theprincipal.net/RSAarcher/apps/ArcherApp/Home.aspx).';
        }
      } catch {
        return 'Unable to validate the LeanIX AppID against the catalog. Verify the ID or contact the EPIC team for assistance.';
      }
    }
    return undefined;
  }

  // Path B — no App ID, and not a governed combination (or Sandbox): blank is OK.
  if (!isRequiredCombo) {
    return undefined;
  }

  // Path C — governed combination with no App ID: the fact-sheet fields must be
  // complete so Repo Foundry can create the fact sheet at execution time.
  if (!name && !orgUnitId && !custodianEmail) {
    return REQUIRED_ERROR;
  }
  if (!name || !orgUnitId || !custodianEmail) {
    return INCOMPLETE_ERROR;
  }
  if (!isEmail(custodianEmail)) {
    return CUSTODIAN_ERROR;
  }
  if (value?.hostedOnAws === undefined) {
    return AWS_REQUIRED_ERROR;
  }
  // Duplicate-name guard (best-effort): block Next when the chosen fact-sheet
  // name is already taken in LeanIX. A checker error never blocks — Repo Foundry
  // remains the final gate at Review -> Create.
  if (ctx.checkNameAvailable) {
    try {
      const available = await ctx.checkNameAvailable(name);
      if (available === false) {
        return nameTakenError(name);
      }
    } catch {
      // ignore — do not block on a transient check failure
    }
  }
  return undefined;
};

export const LeanIXAppIdField = ({
  onChange,
  required,
  formData,
  schema,
  uiSchema,
  formContext,
}: FieldExtensionComponentProps<LeanIXFieldValue>) => {
  // Capture the catalog API for use here and in the validate function.
  catalogApi = useApi(catalogApiRef);

  const options = (uiSchema?.['ui:options'] ?? {}) as LeanIXAppIdOptions;

  // The field value is an object: { appId?, name?, orgUnitId?, custodianEmail?,
  // hostedOnAws? }. Tolerate an undefined initial value.
  const value = (formData ?? {}) as LeanIXFieldValue;

  const form = (formContext?.formData ?? {}) as Record<string, unknown>;
  const type = form[options.typeField ?? 'type'] as string | undefined;
  const lifecycle = form[options.lifecycleField ?? 'lifecycle'] as
    | string
    | undefined;
  const repoUrl = form[options.repoUrlField ?? 'repoUrl'] as string | undefined;
  const isSandbox =
    getRepoOwner(repoUrl) === (options.sandboxOwner ?? DEFAULT_SANDBOX_OWNER);

  // Whether the App ID is mandatory for the current combination. This drives the
  // required `*` directly, so the template needs NO schema-level `required`
  // (which RJSF live-validates on mount and would flag as an error before the
  // user has typed). The rule is still hard-enforced on "Next" by
  // validateLeanIXAppId.
  const requiredTypes = options.requiredTypes ?? ['service', 'website'];
  const requiredLifecycle = options.requiredLifecycle ?? 'production';
  const isRequiredCombo =
    !isSandbox &&
    requiredTypes.includes(type as string) &&
    lifecycle === requiredLifecycle;

  // All rules live in computeLeanIXAppIdError, which the submit-time validate
  // function also calls (so the two never disagree). RJSF runs validate only on
  // "Next" and its inline error is suppressed via `ui:hideError`, so this
  // component owns the visible as-you-type message (debounced; the async catalog
  // check included).
  const [errorText, setErrorText] = useState<string | undefined>(undefined);

  // Merge a patch into the object value (App ID input, or the panel's fields).
  const update = (patch: Partial<LeanIXFieldValue>) => {
    onChange({ ...value, ...patch });
  };

  // Backstage's submit-time async validators only run for fields whose key is
  // present in formData — createAsyncValidators iterates Object.entries(formData)
  // and skips anything absent. A never-touched object field has no key, so
  // validateLeanIXAppId would be skipped entirely, letting a governed combo
  // (production service/website, non-sandbox) pass "Next" with an empty value
  // while the panel stays collapsed. Seed an empty object on mount so the key
  // always exists and the submit-time validator always runs and can block Next.
  useEffect(() => {
    if (formData === undefined) {
      onChange({});
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const appId = value.appId ?? '';
  const hasTypedAppId = appId.trim().length > 0;

  useEffect(() => {
    let active = true;
    // The top field reflects ONLY the App ID text box (format / catalog
    // existence). While the user completes the fact-sheet panel it never turns
    // red — the panel's own required fields guide completion, and submit-time
    // validate still blocks an incomplete panel.
    if (!hasTypedAppId) {
      setErrorText(undefined);
      return () => {
        active = false;
      };
    }
    const handle = setTimeout(() => {
      computeLeanIXAppIdError(value, {
        type,
        lifecycle,
        isSandbox,
        options,
        api: catalogApi,
      })
        .then(err => active && setErrorText(err))
        .catch(() => active && setErrorText(undefined));
    }, 300);
    return () => {
      active = false;
      clearTimeout(handle);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value.appId, hasTypedAppId, type, lifecycle, isSandbox]);

  const hasError = Boolean(errorText);

  // The inline "create a fact sheet" section is offered for the governed combo
  // (production service/website, non-sandbox) while the user has NOT entered an
  // existing App ID. Typing an App ID hides it; clearing it brings it back.
  const showFactSheetPanel =
    options.enableFactSheetCreation === true &&
    isRequiredCombo &&
    !hasTypedAppId;

  // The panel-reference sentence only makes sense while that panel is rendered.
  const guidanceText =
    showFactSheetPanel && options.panelGuidance
      ? `${options.guidance ?? ''} ${options.panelGuidance}`.trim()
      : options.guidance;

  return (
    // The panel is a SIBLING of the FormControl, not a child: a MUI Checkbox
    // inside a FormControl propagates its focus to that FormControl, which would
    // wrongly light up this field's label/underline when the panel's "Hosted on
    // AWS" box is clicked. Keeping the panel outside the FormControl avoids that.
    <>
      <FormControl
        fullWidth
        margin="normal"
        required={required || isRequiredCombo}
        error={hasError}
      >
        <InputLabel htmlFor="leanixAppIdInput">
          {schema.title ?? 'LeanIX Application Inventory External ID'}
        </InputLabel>
        <Input
          id="leanixAppIdInput"
          // Requirement is enforced by validateLeanIXAppId (an existing App ID
          // OR the fact-sheet fields), so the native HTML5 `required` must stay
          // off — otherwise the browser blocks "Next" on the empty box even when
          // the user completed the panel instead. The label keeps its `*`.
          required={false}
          value={appId}
          onChange={e => {
            update({ appId: e.target?.value });
          }}
        />
        {/* Exactly ONE helper line: the validation error while one stands,
            otherwise the persistent guidance — never both, so nothing is
            duplicated. */}
        {(hasError || guidanceText) && (
          <FormHelperText id="leanixAppIdHelperText" error={hasError}>
            {hasError ? errorText : guidanceText}
          </FormHelperText>
        )}
      </FormControl>
      {showFactSheetPanel && (
        <CreateFactSheetPanel value={value} onChange={patch => update(patch)} />
      )}
    </>
  );
};

// Runs on submit. The third `context` arg (modern scaffolder-react) provides the
// WHOLE form (formData) — that is what makes conditional-required possible.
export const validateLeanIXAppId = async (
  value: LeanIXFieldValue,
  validation: FieldValidation,
  context: {
    apiHolder?: ApiHolder;
    formData?: Record<string, any>;
    uiSchema?: any;
  },
) => {
  const options: LeanIXAppIdOptions = context?.uiSchema?.['ui:options'] ?? {};
  const api = context?.apiHolder?.get(catalogApiRef) ?? catalogApi;
  const repoUrl = context?.formData?.[options.repoUrlField ?? 'repoUrl'];
  const isSandbox =
    getRepoOwner(repoUrl) === (options.sandboxOwner ?? DEFAULT_SANDBOX_OWNER);

  // Build the LeanIX name checker from the backend proxy so a duplicate name
  // blocks Next. Absent APIs (older scaffolder-react) simply skip the check.
  const fetchApi = context?.apiHolder?.get(fetchApiRef);
  const configApi = context?.apiHolder?.get(configApiRef);
  const checkNameAvailable =
    fetchApi && configApi
      ? async (name: string): Promise<boolean | undefined> => {
          const baseUrl = configApi.getString('backend.baseUrl');
          const res = await fetchApi.fetch(
            `${baseUrl}/api/repo-foundry/leanix-name-check?name=${encodeURIComponent(
              name,
            )}`,
          );
          if (!res.ok) return undefined;
          const data = await res.json();
          return typeof data?.available === 'boolean'
            ? data.available
            : undefined;
        }
      : undefined;

  const error = await computeLeanIXAppIdError(value, {
    type: context?.formData?.[options.typeField ?? 'type'],
    lifecycle: context?.formData?.[options.lifecycleField ?? 'lifecycle'],
    isSandbox,
    options,
    api,
    checkNameAvailable,
  });

  if (error) {
    // Blocks "Next" while the value is invalid. RJSF's inline rendering of this
    // error is suppressed via `ui:hideError` (see the template), so the field's
    // own as-you-type helper stays the single visible copy — no duplicate.
    validation.addError(error);
  }
};
